"use client";

import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { FileDown, Sparkles, Sheet } from "lucide-react";

import { toast } from "sonner";

interface ParsedRow {
    id: number;
    name: string;
    phone: string;
    address: string;
    product: string;
}

const SAMPLE_DATA = [
    "김영희 010-1234-5678 서울시 마포구 양화로 160 301호 (시카크림 2개)",
    "박민수 010-9876-5432 경기도 성남시 분당구 판교역로 120 101동 2002호 (비타민 1개)",
    "이수진 010-5555-7777 부산 해운대구 우동 1234 센텀아파트 101-505 (선크림)"
];

export function ExcelDemo() {
    const [inputText, setInputText] = useState("");
    const [parsedData, setParsedData] = useState<ParsedRow[]>([]);
    const [isProcessing, setIsProcessing] = useState(false);
    const [sampleIndex, setSampleIndex] = useState(0);

    const handleSamplePaste = () => {
        const nextSample = SAMPLE_DATA[sampleIndex % SAMPLE_DATA.length];
        setInputText(nextSample);
        setSampleIndex(prev => prev + 1);

        // 데이터가 변경되면 결과 초기화 (재정리 유도)
        setParsedData([]);
    };

    const handleParse = async () => {
        if (!inputText.trim()) {
            toast.error("정리할 텍스트를 입력해주세요");
            return;
        }

        if (inputText.trim().length < 20) {
            toast.error("입력값이 너무 짧습니다. 최소 20자 이상 입력해주세요.");
            return;
        }

        const meaninglessPattern = /^[ㅋㅎㅁㄴㄷㄱ\s,.!?~]+$/;
        if (meaninglessPattern.test(inputText.trim())) {
            toast.error("의미 없는 입력은 처리할 수 없습니다.");
            return;
        }

        // Rate Limit Check (Mock)
        const demoUsageCount = parseInt(localStorage.getItem('demo_usage_count') || '0', 10);
        if (demoUsageCount >= 10) {
            toast.error("일일 체험 횟수(10회)를 초과했습니다. 회원가입 후 무제한으로 이용해보세요!");
            return;
        }

        setIsProcessing(true);

        // 샘플 데이터인지 확인 (공백 제거 후 비교)
        const normalize = (str: string) => str.replace(/\s+/g, "").trim();
        const isSample = SAMPLE_DATA.some(sample => normalize(sample) === normalize(inputText));

        if (isSample) {
            // 샘플 데이터는 기존 시뮬레이션 로직 사용
            setTimeout(() => {
                const lines = inputText.trim().split("\n");
                const rows = lines.map((line, index) => {
                    const phoneMatch = line.match(/010[-\s]?\d{3,4}[-\s]?\d{4}/);
                    const phone = phoneMatch ? phoneMatch[0] : "";
                    const productMatch = line.match(/\((.*?)\)/);
                    const product = productMatch ? productMatch[1] : "";
                    const name = line.substring(0, 3);
                    const address = line.replace(name, "").replace(phone, "").replace(`(${product})`, "").trim();

                    return {
                        id: index + 1,
                        name: name.trim(),
                        phone: phone,
                        address: address,
                        product: product || "-"
                    };
                });

                setParsedData(rows);
                setIsProcessing(false);
                localStorage.setItem('demo_usage_count', (demoUsageCount + 1).toString());
                toast.success("깔끔하게 정리되었습니다!");
            }, 800);
        } else {
            // 실제 데이터는 Edge Function 호출
            try {
                const res = await fetch("https://gsvtnnoutinzsbasjvoz.supabase.co/functions/v1/parse-reply", {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImdzdnRubm91dGluenNiYXNqdm96Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDAwNjA3ODMsImV4cCI6MjA1NTYzNjc4M30.nc2HXlY8_dKhPbBbem6nCmaUv_cDe8jsuSh-D13ijhI`
                    },
                    body: JSON.stringify({
                        input: inputText,
                        options: { parseNickname: true, parseProductName: true }
                    })
                });

                const data = await res.json();

                if (!res.ok || !data.output) {
                    throw new Error(data.error || "파싱에 실패했습니다.");
                }

                // 결과 매핑
                const parsedRow: ParsedRow = {
                    id: 1,
                    name: data.output.name || "",
                    phone: data.output.phone || "",
                    address: data.output.address || "",
                    product: data.output.productName || "-"
                };

                // 데모에서는 결과가 1건이어도 배열로 처리
                setParsedData([parsedRow]);
                localStorage.setItem('demo_usage_count', (demoUsageCount + 1).toString());
                toast.success("깔끔하게 정리되었습니다!");
            } catch (err) {
                console.error(err);
                toast.error("정리에 실패했습니다. 다시 시도해주세요.");
            } finally {
                setIsProcessing(false);
            }
        }
    };

    // 다운로드 핸들러 - handleParse 아래에 위치
    const handleDownload = () => {
        toast.success("회원가입 후 다운로드할 수 있습니다!");
    };

    return (
        <div className="flex flex-col lg:flex-row gap-8 w-full max-w-5xl mx-auto p-4 md:p-8 bg-gray-50 rounded-2xl border border-gray-100 shadow-sm">
            {/* 입력 영역 */}
            <div className="flex-1 flex flex-col gap-4">
                <div className="text-center lg:text-left">
                    <div className="flex items-center gap-2 justify-center lg:justify-start mb-2">
                        <h3 className="text-2xl font-bold text-gray-800">배송 정보 자동 정리 체험</h3>
                        <span className="bg-brand-secondary/10 text-brand-secondary px-2 py-0.5 rounded text-xs font-bold">인기</span>
                    </div>

                    <p className="text-gray-600 mb-4">
                        댓글이나 DM으로 받은 정신없는 주문 내역,<br />
                        복사해서 넣기만 하면 엑셀로 정리됩니다.
                    </p>
                </div>

                <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 mb-2">주문 내역 입력</label>
                    <Textarea
                        value={inputText}
                        onChange={(e) => setInputText(e.target.value)}
                        placeholder="여기에 주문 내역을 붙여넣으세요..."
                        className="min-h-[200px] p-4 resize-none bg-white border-gray-300 focus:border-brand-primary text-base"
                    />
                </div>

                <div className="flex flex-col gap-2">
                    <Button
                        variant="outline"
                        onClick={handleSamplePaste}
                        className="w-full h-12 text-lg border-brand-secondary text-brand-secondary hover:bg-brand-secondary/10 font-bold transition-all"
                    >
                        <Sparkles className="w-5 h-5 mr-2" />
                        샘플 데이터 채워보기
                    </Button>
                    <Button
                        onClick={handleParse}
                        disabled={isProcessing}
                        className="w-full h-12 text-lg bg-brand-secondary hover:bg-brand-secondary/90 text-white font-bold shadow-md hover:shadow-lg transition-all"
                    >
                        {isProcessing ? "정리하는 중..." : "버튼 하나로 정리하기"}
                    </Button>
                </div>
            </div>

            {/* 결과 미리보기 영역 */}
            <div className="flex-1 flex flex-col">
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden flex flex-col h-full min-h-[300px]">
                    <div className="bg-green-50 px-4 py-3 border-b border-green-100 flex justify-between items-center">
                        <span className="font-semibold text-green-800 flex items-center gap-2">
                            <img src="/assets/excel-icon.png" alt="Excel" className="w-5 h-5 object-contain" onError={(e) => e.currentTarget.style.display = 'none'} />
                            엑셀 미리보기
                        </span>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={handleDownload}
                            className="h-8 text-green-700 hover:text-green-800 hover:bg-green-100"
                        >
                            <FileDown className="w-4 h-4 mr-1" />
                            다운로드
                        </Button>
                    </div>

                    <div className="overflow-x-auto flex-1">
                        {parsedData.length > 0 ? (
                            <Table>
                                <TableHeader>
                                    <TableRow className="bg-gray-50 hover:bg-gray-50">
                                        <TableHead className="w-[80px]">이름</TableHead>
                                        <TableHead className="w-[120px]">전화번호</TableHead>
                                        <TableHead>주소</TableHead>
                                        <TableHead className="w-[100px]">상품</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {parsedData.map((row) => (
                                        <TableRow key={row.id}>
                                            <TableCell className="font-medium">{row.name}</TableCell>
                                            <TableCell>{row.phone}</TableCell>
                                            <TableCell className="text-xs text-gray-600">{row.address}</TableCell>
                                            <TableCell className="text-xs">{row.product}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        ) : (
                            <div className="h-full flex flex-col items-center justify-center text-gray-400 gap-4 p-8">
                                <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center">
                                    <Sheet className="w-8 h-8 text-gray-300" />
                                </div>
                                <p className="text-center">
                                    왼쪽에서 '정리하기' 버튼을 누르면<br />
                                    여기에 깔끔하게 정리되어 나타납니다
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
